import { useState, useEffect, useRef } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export const ClientDetailsCard = ({ client }) => {
  const qc = useQueryClient();
  const [form, setForm] = useState({
    name: client.name || "",
    abn_acn: client.abn_acn || "",
    client_type: client.client_type || "",
    industry: client.industry || "",
    notes: client.notes || "",
  });
  const [state, setState] = useState(null);
  const firstRef = useRef(true);
  const dirtyRef = useRef(false);

  const { data: types = [] } = useQuery({
    queryKey: ["client-types"],
    queryFn: async () => (await api.get("/clients/client-types")).data,
  });

  // Auto-save: any edit persists after a 1.2s pause — no Save button needed.
  useEffect(() => {
    if (firstRef.current) { firstRef.current = false; return; }
    dirtyRef.current = true;
    setState("editing");
    const t = setTimeout(async () => {
      if (!dirtyRef.current) return;
      if (!form.name.trim()) { setState("Name required — not saved"); return; }
      setState("saving");
      try {
        const payload = { name: form.name.trim(), abn_acn: form.abn_acn, industry: form.industry, notes: form.notes };
        if (form.client_type) payload.client_type = form.client_type;
        await api.patch(`/clients/${client.id}`, payload);
        dirtyRef.current = false;
        setState(`Saved ${new Date().toLocaleTimeString("en-AU", { hour: "numeric", minute: "2-digit" })}`);
        qc.invalidateQueries({ queryKey: ["client", client.id] });
        qc.invalidateQueries({ queryKey: ["clients"] });
      } catch {
        setState("Save failed — retrying on next edit");
      }
    }, 1200);
    return () => clearTimeout(t);
  }, [form]); // eslint-disable-line react-hooks/exhaustive-deps

  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  return (
    <Card className="rounded-sm p-4 industrial-shadow" data-testid="client-details-card">
      <div className="flex items-center justify-between mb-3 gap-2">
        <div className="stamp-label text-slate-500">Business Details — saves automatically</div>
        <span
          className={`text-[11px] font-mono shrink-0 ${state === "saving" || state === "editing" ? "text-slate-400" : state?.startsWith("Saved") ? "text-emerald-600" : "text-rose-500"}`}
          data-testid="client-autosave-state"
        >
          {state === "saving" ? "Saving…" : state === "editing" ? "…" : state}
        </span>
      </div>
      <div className="grid grid-cols-2 gap-3">
        <div className="col-span-2">
          <Label className="stamp-label text-slate-600">Business / client name *</Label>
          <Input data-testid="client-edit-name" value={form.name} onChange={set("name")} className="rounded-sm h-9 mt-1" />
        </div>
        <div>
          <Label className="stamp-label text-slate-600">ABN / ACN</Label>
          <Input data-testid="client-edit-abn" value={form.abn_acn} onChange={set("abn_acn")} className="rounded-sm h-9 mt-1 font-mono" />
        </div>
        <div>
          <Label className="stamp-label text-slate-600">Client type</Label>
          <Select value={form.client_type} onValueChange={(v) => setForm((f) => ({ ...f, client_type: v }))}>
            <SelectTrigger data-testid="client-edit-type" className="rounded-sm h-9 mt-1"><SelectValue placeholder="Select type" /></SelectTrigger>
            <SelectContent>{types.map((t) => <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>)}</SelectContent>
          </Select>
        </div>
        <div className="col-span-2">
          <Label className="stamp-label text-slate-600">Industry</Label>
          <Input data-testid="client-edit-industry" value={form.industry} onChange={set("industry")} className="rounded-sm h-9 mt-1" />
        </div>
        <div className="col-span-2">
          <Label className="stamp-label text-slate-600">Internal notes</Label>
          <Textarea data-testid="client-edit-notes" rows={3} value={form.notes} onChange={set("notes")} className="rounded-sm mt-1 text-sm" />
        </div>
      </div>
      <div className="text-[11px] text-slate-400 mt-3">
        Customer #: <span className="font-mono text-cyan-600 font-semibold">{client.customer_number || "—"}</span>
      </div>
    </Card>
  );
};
